import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { AuthenticatedClient } from '@stitch-fix/client-facing-auth';
import { getWindow } from '@stitch-fix/mode-react';
import { D, G } from '@mobily/ts-belt';
import settings from '../../settings';
import { formatMessage, DEFAULT_LEVEL, type LoggerArgs } from './common';
export const initDatadogLogger = () => {
  const win = getWindow();
  if (!win) return;
  const clientToken = settings.datadogClientToken;
  if (!clientToken) return;
  datadogLogs.init({
    clientToken,
    service: `${settings.appName}-browser`,
    forwardErrorsToLogs: false,
    sessionSampleRate: 100
  });
};
type BrowserLoggerArgs = LoggerArgs & {
  /**
   * An optional JavaScript Error instance associated with the log entry
   * to enable Datadog to extract stack traces and error information.
   *
   * For developer convenience, the type is `unknown` to allow any value to be passed in.
   */
  error?: unknown;
};

/**
 * https://docs.datadoghq.com/logs/log_collection/javascript/#generic-logger-function
 * Logs can be found in Datadog under `service:growth-next-ui-browser`
 */
export const browserLogger = ({
  level = DEFAULT_LEVEL,
  message,
  team,
  feature,
  context = {},
  error
}: BrowserLoggerArgs) => {
  datadogLogs.logger.log(formatMessage(message), {
    team,
    feature,
    ...context
  }, level,
  // Datadog automatically extracts error information if the fourth parameter is an Error instance
  error && error instanceof Error ? error : undefined);
};

/**
 * https://docs.datadoghq.com/logs/log_collection/javascript/#global-context
 */
type ExtendedBrowserLoggerContext = {
  clientId: string;
  featureFlags?: {
    [featureFlagName: string]: boolean;
  };
};
export const addToBrowserLoggerContext = ({
  clientId,
  featureFlags
}: ExtendedBrowserLoggerContext) => {
  const globalContext = datadogLogs.getGlobalContext();
  const extendedFeatureFlags = D.merge(G.isObject(globalContext.featureFlags) ? globalContext.featureFlags : {}, featureFlags ?? {});
  datadogLogs.setGlobalContext({
    ...globalContext,
    associatedClient: {
      ...(G.isObject(globalContext.associatedClient) && globalContext.associatedClient),
      id: clientId
    },
    ...(D.isNotEmpty(extendedFeatureFlags) && {
      featureFlags: extendedFeatureFlags
    })
  });
};

/**
 * Client-analytics-service allows us to add RUM context only at the time of initialization,
 * with `initPerformanceAnalytics` in `instrumentation-client.ts`, [source](https://github.com/stitchfix/sf-next/blob/a76d2d7a2d447efe4fd6bef154fa2d7e565bf4a9/src/components/utils/analytics.ts#L56-L59)
 *
 * Since we don't have acces to the business-line during the initialization,
 * we need to use Datadog's RUM API to add the business-line to the RUM context,
 * [docs](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#add-global-context-property)
 */
export const addBusinessLineToRumContext = (businessLine: AuthenticatedClient['businessLine']) => {
  if (!businessLine) return;
  datadogRum.setGlobalContextProperty('businessLine', businessLine);
};

// From docs: The following special characters are not supported for the Feature Flag name:
// ., :, +, -, =, &&, ||, >, <, !, (, ), {, }, [, ], ^, ", “, ”, ~, *, ?, \
const invalidFeatureFlagNameChars = /[ .:+\-=&&|><!()[\]{}^"“”~*?\\]/g;

/**
 * Enable feature flag tracking in Datadog RUM
 * [docs](https://docs.datadoghq.com/real_user_monitoring/feature_flag_tracking/using_feature_flags)
 */
export const addFeatureFlagsToRumContext = (featureFlags: {
  [featureFlagName: string]: boolean | string;
}) => {
  if (!datadogRum.addFeatureFlagEvaluation) return;
  for (const [name, value] of Object.entries(featureFlags)) {
    const sanitizedName = name.replace(invalidFeatureFlagNameChars, '_');
    datadogRum.addFeatureFlagEvaluation(sanitizedName, value);
  }
};

/**
 * Logging an error to console will be picked up by datadog logs
 */
export const logError = (error: unknown, ...params: unknown[]) => {
  // eslint-disable-next-line no-console
  console.error(error, ...params);
};